import { useState, useEffect } from 'react';
import { Sun, Plug, Zap, Wind, AlertTriangle, AlertCircle, MapPin, Wifi } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatTimestamp, cn } from '@/lib/utils';
import { resolveSystemMetrics, getSystemIconColor, chargeSlotAppearance } from '@/components/unit/SystemStatusIcons.helpers';
import { legacyIconState } from '@/lib/legacyStatusIcons';
import { BatteryDirectionMark } from '@/components/unit/BatteryDirectionMark';
import { BatteryCriticalTag } from '@/components/unit/BatteryCriticalTag';
import { getLocationTextClass } from '@/lib/locationUtils';
import { isValidLocation } from '@/components/map/mapConfig';
import type { Unit } from '@/types';

/** Session cache of loaded image URLs - skip grey skeleton when re-hovering same unit */
const loadedImageUrls = new Set<string>();

interface UnitPreviewCardProps {
  unit: Unit;
  isMobile: boolean;
  onAction: (action: 'view' | 'solar' | 'charger' | 'hvac' | 'starlink') => void;
  imageHeight?: string;
  className?: string;
}

export function UnitPreviewCard({
  unit,
  isMobile,
  onAction,
  imageHeight = 'h-24',
  className
}: UnitPreviewCardProps) {
  // The quick-metrics grid's charger figure (unchanged).
  const { chargerPower } = resolveSystemMetrics(unit);
  // The original icons, lit from the server's live_status (Eric, 2026-10-30: "Put the old icons
  // back exactly"); App's band colours the pill, "—" for no reading.
  const icons = legacyIconState(unit.live_status);
  const batteryText = icons.batteryLevel == null ? '—' : `${icons.batteryLevel}%`;
  const starlinkDownload = unit.systems?.starlink?.currentMetrics?.downloadSpeed;

  // Session cache of loaded image URLs - skip grey skeleton when image was already shown
  const [imageLoaded, setImageLoaded] = useState(() => loadedImageUrls.has(unit.image ?? ''));
  const [imageSrc, setImageSrc] = useState(unit.image);

  // Reset loading state when unit changes
  useEffect(() => {
    const url = unit.image ?? '';
    setImageLoaded(loadedImageUrls.has(url));
    setImageSrc(url);
  }, [unit.image]);

  const handleImageLoad = () => {
    if (unit.image) loadedImageUrls.add(unit.image);
    setImageLoaded(true);
  };

  return (
    <TooltipProvider delayDuration={isMobile ? 0 : 300}>
      <div 
        className={cn("bg-card overflow-hidden flex flex-col h-full", className)}
        onClick={(e) => {
          e.stopPropagation();
          onAction('view');
        }}
      >
        {/* Unit Image - Flexible height to fill available space */}
        <div className={cn("relative bg-muted overflow-hidden", imageHeight === 'h-full' ? 'flex-1 min-h-0' : imageHeight)}>
          {/* Loading skeleton */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse" />
          )}
          <img
            src={imageSrc}
            alt={unit.name}
            loading="eager"
            decoding="async"
            className={cn(
              "w-full h-full object-cover transition-opacity duration-150",
              imageLoaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={handleImageLoad}
            onError={() => {
              setImageSrc('/assets/units/placeholder.jpg');
            }}
          />
          
          {/* Group Name Chip - Top Left */}
          {unit.primary_group?.name && (
            <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 backdrop-blur-sm rounded text-[10px] text-white font-medium z-10">
              {unit.primary_group.name}
            </div>
          )}

          {/* Last Updated Chip - Bottom Right */}
          <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 backdrop-blur-sm rounded text-[10px] text-white font-medium">
            Updated {formatTimestamp(unit.lastUpdate)}
          </div>
        </div>

        {/* Header with status and name */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50">
          {/* Status Indicator */}
          <span
            className={cn(
              'h-2.5 w-2.5 rounded-full flex-shrink-0',
              unit.status === 'online' ? 'bg-success' :
              unit.status === 'idle' ? 'bg-muted-foreground' :
              unit.status === 'offline' ? 'bg-error' :
              'bg-muted-foreground/50'
            )}
          />

          {/* Unit Name & Location */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{unit.unit_code}</span>
              {/* Dual alert icons: warning (triangle) and fault (circle) */}
              {(unit.warning_count ?? 0) > 0 && (
                <AlertTriangle className="h-3 w-3 flex-shrink-0 text-warning" />
              )}
              {(unit.fault_count ?? 0) > 0 && (
                <AlertCircle className="h-3 w-3 flex-shrink-0 text-fault" />
              )}
            </div>
            <div className="flex items-center gap-1 mt-0.5 text-[10px] text-muted-foreground/70">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              {isValidLocation(unit.location) ? (
                <span className={cn('truncate', getLocationTextClass(unit.locationSource))}>{unit.location?.address || 'Located'}</span>
              ) : (
                <span className="truncate">No GPS fix</span>
              )}
              {/* Location-source badge removed from the map preview card per Eric — it
                  still shows on the unit detail page (UnitDetailView). */}
            </div>
          </div>

          {/* Battery Indicator */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* At 15% or less the Critical word sits under the reading, beside the tall pill,
                so a phone-width card keeps room for the unit code. */}
            <div className="flex flex-col items-end gap-0.5">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-muted-foreground min-w-[28px] text-right">{batteryText}</span>
                <BatteryDirectionMark direction={icons.direction} fill={icons.batteryFill} className="text-[9px]" />
              </div>
              <BatteryCriticalTag show={icons.critical} />
            </div>
            <div className="w-4 h-7 bg-background/50 rounded-sm border border-border/50 flex flex-col-reverse overflow-hidden">
              <div
                className={cn('w-full transition-all', icons.batteryFill)}
                style={{ height: `${icons.batteryLevel ?? 0}%` }}
              />
            </div>
          </div>

          {/* System Icons */}
          <div className="flex gap-3 items-center flex-shrink-0">
            <Tooltip delayDuration={isMobile ? 0 : 300}>
              <TooltipTrigger asChild>
                <div
                  onClick={(e) => { e.stopPropagation(); onAction('solar'); }}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  {/* On shore power the Alerts board's plug takes the sun's spot (Eric: "yes plug"). */}
                  {icons.plug ? (
                    <Plug className={cn('h-4 w-4', chargeSlotAppearance(icons.plug).colorClass)} />
                  ) : (
                    <Sun
                      className={cn(
                        'h-4 w-4',
                        getSystemIconColor('solar', icons.solarOn)
                      )}
                    />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent isMobile={isMobile}>
                Sol-Ark: {batteryText} SoC{icons.plug
                  ? ` · ${chargeSlotAppearance(icons.plug).title}`
                  : icons.solarKw != null ? ` · ${icons.solarKw.toFixed(1)} kW` : ''}
              </TooltipContent>
            </Tooltip>

            <Tooltip delayDuration={isMobile ? 0 : 300}>
              <TooltipTrigger asChild>
                <div
                  onClick={(e) => { e.stopPropagation(); onAction('charger'); }}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <Zap
                    className={cn(
                      'h-4 w-4',
                      getSystemIconColor('charger', icons.chargerOn)
                    )}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent isMobile={isMobile}>
                Tesla: {icons.chargerOn
                  ? (icons.chargerKw != null ? `Charging · ${Math.round(icons.chargerKw)} kW` : 'Charging')
                  : unit.systems?.charger?.status === 'disconnected' ? 'Disconnected' : 'Ready'}
              </TooltipContent>
            </Tooltip>

            <Tooltip delayDuration={isMobile ? 0 : 300}>
              <TooltipTrigger asChild>
                <div
                  onClick={(e) => { e.stopPropagation(); onAction('hvac'); }}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <Wind
                    className={cn(
                      'h-4 w-4',
                      getSystemIconColor('hvac', icons.hvacOn)
                    )}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent isMobile={isMobile}>
                HVAC: {unit.systems?.hvac?.temperature ?? '--'}°F
              </TooltipContent>
            </Tooltip>

            <Tooltip delayDuration={isMobile ? 0 : 300}>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); onAction('starlink'); }}
                  className="p-1 transition-opacity active:opacity-70 cursor-pointer hover:opacity-80"
                  aria-label="Starlink"
                >
                  <Wifi
                    className={cn(
                      'h-4 w-4',
                      getSystemIconColor('starlink', icons.starlinkOn)
                    )}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent isMobile={isMobile}>
                Starlink: {!icons.starlinkOn
                  ? 'Offline'
                  : starlinkDownload == null
                    // `?? 0` here restored a fabricated "0 Mbps" for an online
                    // terminal that simply has not reported a speed yet.
                    ? 'N/A'
                    : `${Math.round(starlinkDownload)} Mbps`}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Quick Metrics Grid */}
        <div className="grid grid-cols-4 gap-px bg-border/50 border-t border-border/50">
          {/* Solar */}
          <div
            onClick={(e) => { e.stopPropagation(); onAction('solar'); }}
            className="bg-card p-2 aspect-[10/7] text-center flex flex-col justify-center space-y-0.5 hover:bg-[#8FB560]/10 transition-colors cursor-pointer group"
          >
            <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider group-hover:text-[#8FB560] transition-colors">Solar</div>
            <div className="text-xs font-semibold">
              {unit.systems?.solar?.dailyGeneration ? Math.round(unit.systems.solar.dailyGeneration) : 0} <span className="text-[10px] font-normal text-muted-foreground">kWh</span>
            </div>
          </div>

          {/* Tesla */}
          <div 
            onClick={(e) => { e.stopPropagation(); onAction('charger'); }}
            className="bg-card p-2 aspect-[10/7] text-center flex flex-col justify-center space-y-0.5 hover:bg-[#8FB560]/10 transition-colors cursor-pointer group"
          >
            <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider group-hover:text-[#8FB560] transition-colors">Charger</div>
            <div className="text-xs font-semibold">
              {chargerPower ? Math.round(chargerPower) : 0} <span className="text-[10px] font-normal text-muted-foreground">kW</span>
            </div>
          </div>

          {/* HVAC */}
          <div 
            onClick={(e) => { e.stopPropagation(); onAction('hvac'); }}
            className="bg-card p-2 aspect-[10/7] text-center flex flex-col justify-center space-y-0.5 hover:bg-[#8FB560]/10 transition-colors cursor-pointer group"
          >
            <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider group-hover:text-[#8FB560] transition-colors">HVAC</div>
            <div className="text-xs font-semibold">
              {unit.systems?.hvac?.temperature ? Math.round(unit.systems.hvac.temperature) : '--'} <span className="text-[10px] font-normal text-muted-foreground">°F</span>
            </div>
          </div>

          {/* Starlink */}
          <div 
            onClick={(e) => { e.stopPropagation(); onAction('starlink'); }}
            className="bg-card p-2 aspect-[10/7] text-center flex flex-col justify-center space-y-0.5 hover:bg-[#8FB560]/10 transition-colors cursor-pointer group"
          >
            <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider group-hover:text-[#8FB560] transition-colors">Starlink</div>
            <div className="text-xs font-semibold whitespace-nowrap">
              {unit.systems?.starlink?.currentMetrics?.downloadSpeed != null &&
               unit.systems.starlink.currentMetrics.uploadSpeed != null ? (
                <>
                  {Math.round(unit.systems.starlink.currentMetrics.downloadSpeed)}<span className="text-muted-foreground/70">/</span>{Math.round(unit.systems.starlink.currentMetrics.uploadSpeed)}
                </>
              ) : (
                <span className="text-muted-foreground">-/-</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

